import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { withRouter } from 'react-router-dom';
import { branch, lifecycle, renderComponent, renderNothing, withHandlers, withProps } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { defaultIndustrySettings, routes, tabKeys } from '../../../../constants';
import { businessActions, businessCurrentTeamSelector, businessSelector } from '../../../../state/modules/business';
import { currentUserIndustry, currentUserSelector } from '../../../../state/modules/user';
import { possessiveCase } from '../../../../utils';
import Loading from '../../loading';
import AccountSettings from '../account-settings';
import { CpdSettings } from '../account-settings/components';

const TeamTrainingPlan = compose(
  connect((state) => ({
    business: businessSelector(state),
    userIndustryType: fp.flow(currentUserIndustry, fp.first, fp.get('industryType'))(state),
    industryCpdCategorys: state.industry.cpdCategorys,
    industryRole: fp.head(state.business.team.industryRoles),
    industryRolesIsFetching: state.business.team.isFetchingTeamIndustryRoles,
    isFetchingIndustry: state.industry.isFetching,
    isUpdating: state.business.team.isUpdating,
    isFetchingTeamCpdCategorys: state.business.team.isFetchingTeamCpdCategorys,
    teamTrainingPlan: state.business.team.trainingPlan,
    user: currentUserSelector(state),
    error: state.business.team.error
  }), (dispatch) => ({
    getTeamIndustryRoles: bindActionCreators(businessActions.business.team.getTeamIndustryRoles, dispatch),
    getTeamTrainingPlan: bindActionCreators(businessActions.business.team.getTeamTrainingPlan, dispatch),
    saveTeamTrainingPlan: bindActionCreators(businessActions.business.team.setTeamTrainingPlan, dispatch)
  })),
  branch(
    ({ business, team }) => !business || !team,
    renderNothing
  ),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTeamIndustryRoles,
        getTeamTrainingPlan,
        industryRolesIsFetching,
        isFetchingTeamCpdCategorys,
        team
      } = this.props;

      if (!industryRolesIsFetching) {
        getTeamIndustryRoles(business.id, team.businessTeam.id);
      }

      if (!isFetchingTeamCpdCategorys) {
        getTeamTrainingPlan(business.id, team.businessTeam.id);
      }
    }
  }),
  branch(
    ({ industryRole, teamTrainingPlan }) => !industryRole || !teamTrainingPlan,
    renderComponent(Loading)
  ),
  withProps(({
    industryRole,
    industryRolesIsFetching,
    isFetchingTeamCpdCategorys,
    teamTrainingPlan
  }) => ({
    cpdCategorys: teamTrainingPlan,
    isLoading: isFetchingTeamCpdCategorys || industryRolesIsFetching,
    industry: {
      ...defaultIndustrySettings,
      ...industryRole
    },
    cpdCategoryTransform: (cpdCategory) => ({
      ...cpdCategory,
      businessTeamId: 0 // Dummy user id so the server accepts the JSON.
    }),
    headings: {
      cpdCategorys: [
        <p key='heading'>
          <strong>
            Your team CPD categories
          </strong>
        </p>,
        <p key='copy'>
          Specify the knowledge areas your team is permitted to provide general and personal advice for.
        </p>
      ],
      cpdDate: [
        <p key='heading'>
          <strong>
            Enter the last day of the CPD Year
          </strong>
        </p>,
        <p key='copy'>
          Your CPD anniversary date is the annual year in which your CPD track against.
          Our default CPD year runs from July 1 - June 30.
          You can change this at any time.
        </p>
      ],
      cpdPoints: [
        <p key='heading'>
          <strong>
            How many CPD hours does your team need to earn in a year?
          </strong>
        </p>,
        <p key='copy'>
          Specify the total number of CDP hours your team is required to complete each year.
        </p>
      ],
      cpdAllocation: [
        <p key='heading'>
          <strong>
            CPD allocation
          </strong>
        </p>,
        <p key='copy'>
          Specify the number of CPD hours your team is required to meet for each CPD category you provide services on.
        </p>
      ]
    }
  })),
  withHandlers({
    saveCpdCategorys: ({
      saveTeamTrainingPlan,
      user,
      business: {
        id: businessId
      },
      team: {
        businessTeam: {
          id: businessTeamId
        }
      }
    }) => ({
      industry,
      ...payload
    }) => saveTeamTrainingPlan({
      ...payload,
      industry,
      businessId,
      businessTeamId,
      userId: user.id
    })
  })
)(CpdSettings);

const TeamSettings = compose(
  withRouter,
  withProps(({
    match: {
      params: {
        user
      }
    }
  }) => ({
    teamId: parseInt(user, 10)
  })),
  connect((state) => ({
    contextTeam: businessCurrentTeamSelector(state),
    business: businessSelector(state)
  }), (dispatch) => ({
    getTeam: bindActionCreators(businessActions.business.team.getTeam, dispatch)
  })),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTeam,
        teamId
      } = this.props
      getTeam(business.id, teamId);
    }
  }),
  branch(
    ({ contextTeam }) => !contextTeam,
    renderComponent(Loading)
  ),
  withProps(({
    contextTeam,
    teamId
  }) => ({
    routes: (tabKey) => routes.app.teams.teamSettings(teamId, tabKey),
    title: `${possessiveCase(he.decode(contextTeam.businessTeam.name))} Settings`,
    tabs: [
      {
        path: tabKeys.businessTeamSettings.trainingPlan,
        title: 'Training plan',
        content: <TeamTrainingPlan team={contextTeam} planOwner={he.decode(contextTeam.businessTeam.name)} />
      }
    ]
  })),
  withProps(({
    title,
    contextTeam
  }) => ({
    breadcrumb:
      <Breadcrumb>
        <LinkContainer to={routes.app.teams.root}>
          <BreadcrumbItem>
            Teams
          </BreadcrumbItem>
        </LinkContainer>
        <LinkContainer to={routes.app.teams.teamMembers(contextTeam.businessTeam.id)}>
          <BreadcrumbItem>
            {he.decode(contextTeam.businessTeam.name)}
          </BreadcrumbItem>
        </LinkContainer>
        <BreadcrumbItem active>
          {title}
        </BreadcrumbItem>
      </Breadcrumb>
  }))
)(AccountSettings);

export default TeamSettings; 



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/team-settings.js